import { StateAcronym } from "@gipi-shared/enums/state-acronym.enum";
import { StringUtil } from "@gipisistemas/ng-core";

export const stateNamesMap: Record<StateAcronym, string> = {
    AC: 'Acre',
    AL: 'Alagoas',
    AP: 'Amapá',
    AM: 'Amazonas',
    BA: 'Bahia',
    CE: 'Ceará',
    DF: 'Distrito Federal',
    ES: 'Espírito Santo',
    GO: 'Goiás',
    MA: 'Maranhão',
    MG: 'Minas Gerais',
    MT: 'Mato Grosso',
    MS: 'Mato Grosso do Sul',
    PA: 'Pará',
    PB: 'Paraíba',
    PE: 'Pernambuco',
    PI: 'Piauí',
    PR: 'Paraná',
    RJ: 'Rio de Janeiro',
    RN: 'Rio Grande do Norte',
    RO: 'Rondônia',
    RS: 'Rio Grande do Sul',
    RR: 'Roraima',
    SC: 'Santa Catarina',
    SE: 'Sergipe',
    SP: 'São Paulo',
    TO: 'Tocantins',
};

export class AcbrIEUtil {

    private static _stateName(uf: StateAcronym, full: boolean = false): string {
        const name = stateNamesMap[uf as StateAcronym];
        if (!name) {
            return uf;
        }
        return full ? `(${uf}) - ${name}` : name;
    }

    /**
     * Verifica se um número de inscrição estadual é válido para o estado especificado.
     * @param state O estado para o qual a inscrição estadual será validada.
     * @param stateRegistration O número de inscrição estadual a ser verificado.
     * @param options Opções adicionais para a validação, como `adjustSize` e `displayCorrectDigit`.
     * - `adjustSize`: Se `true`, ajusta o size da inscrição estadual.
     * - `displayCorrectDigit`: Se `true`, exibe o dígito verificador correto.
     * @returns `true` se a inscrição estadual for válida, caso contrário, `false`.
     * @example
     * DocumentUtil.isValidIE('GO', '113687214'); // Retorna `{ isValid: true, message: '' }`.
     * DocumentUtil.isValidIE('AC', '0185402241495'); // Retorna `{ isValid: false, message: 'Inscrição estadual inválida para AC. Motivo: Dígito verificador inválido.' }`.
     */
    public static isValidIE(uf: StateAcronym, stateRegistration: string, options?: { adjustSize?: boolean; displayCorrectDigit?: boolean },): { isValid: boolean; message?: string; } {
        const cIgnorarChar: string = './-';

        const strIsNumber = (str: string) => {
            return /^\d+$/.test(str);
        };

        const charIsNum = (str: string) => {
            return /^[0-9]$/.test(str);
        };

        const strToIntDef = (str: string, def: number) => {
            if (StringUtil.isEmpty(str)) {
                return def;
            }
            const numberish = parseInt(str);
            return isNaN(numberish) ? def : numberish;
        };

        const padLeft = (str: string, length: number, padChar = ''): string => {
            if (StringUtil.isEmpty(padChar)) {
                padChar = ' ';
            }
            return str.padStart(length, padChar);
        };

        const clearDocument = (str: string) => {
            let result = '';
            for (const char of str) {
                if (!cIgnorarChar.includes(char)) {
                    result += char.toUpperCase();
                }
            }
            return result.trim();
        };

        const result = (isValid: boolean, message: string = '') => {
            return {
                isValid,
                message,
            };
        };

        const C0_9: string = '0-9';
        const positionWeights: number[][] = [
            /*       1  2  3  4  5  6  7  8  9  10 11 12 13 14 */
            /* 01 */[0, 2, 3, 4, 5, 6, 7, 8, 9, 2, 3, 4, 5, 6],
            /* 02 */[0, 0, 2, 3, 4, 5, 6, 7, 8, 9, 2, 3, 4, 5],
            /* 03 */[2, 0, 3, 4, 5, 6, 7, 8, 9, 2, 3, 4, 5, 6],
            /* 04 */[0, 2, 3, 4, 5, 6, 0, 0, 0, 0, 0, 0, 0, 0],
            /* 05 */[0, 8, 7, 6, 5, 4, 3, 2, 1, 0, 0, 0, 0, 0],
            /* 06 */[0, 2, 3, 4, 5, 6, 7, 0, 0, 8, 9, 0, 0, 0],
            /* 07 */[0, 2, 3, 4, 5, 6, 7, 8, 9, 1, 2, 3, 4, 5],
            /* 08 */[0, 2, 3, 4, 5, 6, 7, 2, 3, 4, 5, 6, 7, 8],
            /* 09 */[0, 0, 2, 3, 4, 5, 6, 7, 2, 3, 4, 5, 6, 7],
            /* 10 */[0, 0, 2, 1, 2, 1, 2, 1, 2, 1, 1, 2, 1, 0],
            /* 11 */[0, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 2, 3, 0],
            /* 12 */[0, 0, 0, 0, 10, 8, 7, 6, 5, 4, 3, 1, 0, 0],
            /* 13 */[0, 2, 3, 4, 5, 6, 7, 8, 9, 10, 2, 3, 0, 0],
            /* 14 */[0, 0, 2, 3, 4, 5, 6, 7, 8, 3, 4, 5, 6, 7],
        ];

        let vDigits: string[] = ['', '', '', '', '', '', '', '', '', '', '', '', '', ''];
        let xROT: string = 'E';
        let yROT: string = '';
        let size: number = 0;
        let factorF: number = 0;
        let factorG: number = 0;
        let index: number;
        let xMD: number = 11;
        let xTP: number = 1;
        let yMD: number = 0;
        let yTP: number = 0;
        let checkDigit: number;
        let dvX: number;
        let dvY: number;
        let sum: number;
        let sumQ: number;
        let nD: number;
        let weight: number;
        let valid: boolean;
        let step: string;
        let digit: string;
        let lPrefixo: string;

        let documentIE: string = clearDocument(stateRegistration);
        let errorMessage: string = '';
        let calculatedDigit: string = '';

        if (StringUtil.isEmpty(documentIE) || documentIE.trim().toUpperCase() === 'ISENTO') {
            return result(true);
        }

        if (StringUtil.isEmpty(documentIE)) {
            errorMessage = 'Inscrição estadual não pode ser vazio.';
            return result(false, errorMessage);
        }

        if (StringUtil.isEmpty(uf)) {
            errorMessage = 'Informe a UF para que seja realizada a validação';
            return result(false, errorMessage);
        }

        /**
         * Somente digitos ou letra P na primeira posição
         * P é usado pela inscrição estadual de produtor rural de SP
         */
        if (
            !strIsNumber(documentIE.substring(1)) || // copia do 2 até o fim
            (!charIsNum(documentIE[0]) && documentIE[0] !== 'P') // primeiro char não é número nem 'P'
        ) {
            errorMessage = 'Caracteres inválidos na inscrição estadual';
            return result(false, errorMessage);
        }

        if (uf === 'AC') {
            if (documentIE.length === 9) {
                size = 9;
                vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '1', '0', '', '', '', '', ''];
            } else {
                size = 13;
                xTP = 2;
                yROT = 'E';
                yMD = 11;
                yTP = 1;
                vDigits = ['DVY', 'DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '1', '0', '',];
            }
        }

        if (uf === 'AL') {
            size = 9;
            xROT = 'BD';
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '4', '2', '', '', '', '', ''];
        }

        if (uf === 'AP') {
            size = 9;
            xROT = 'CE';
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '3', '0', '', '', '', '', ''];

            if (documentIE >= '030170010' && documentIE <= '030190229') {
                factorF = 1;
            } else if (documentIE >= '030190230') {
                xROT = 'E';
            }
        }

        if (uf === 'AM') {
            size = 9;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '', '', '', '', ''];
        }

        if (uf === 'BA') {
            if (documentIE.length < 9) {
                documentIE = padLeft(documentIE, 9, '0');
            }

            size = 9;
            xTP = 2;
            yTP = 3;
            yROT = 'E';
            vDigits = ['DVX', 'DVY', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '', '', '', '', ''];

            // Delphi é 1-based, JS é 0-based
            if ('0123458'.includes(documentIE[1])) {
                xMD = 10;
                yMD = 10;
            } else {
                xMD = 11;
                yMD = 11;
            }
        }

        if (uf === 'CE') {
            size = 9;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '0', '', '', '', '', ''];
        }

        if (uf === 'DF') {
            size = 13;
            xTP = 2;
            yROT = 'E';
            yMD = 11;
            yTP = 1;
            vDigits = ['DVY', 'DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '7,8', '0', '',];
        }

        if (uf === 'ES') {
            size = 9;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '', '', '', '', ''];
        }

        if (uf === 'GO') {
            size = 9;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '1,2', '', '', '', '', ''];

            // Delphi é 1-based, JS é 0-based
            lPrefixo = documentIE[0] + documentIE[1];

            const prefixoValido = [10, 11, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29];
            if (!prefixoValido.includes(parseInt(lPrefixo))) {
                errorMessage = 'Prefixo IE Inválido 10 ou 11 / 20 a 29';
            }

            if (documentIE >= '101031050' && documentIE <= '101199979') {
                factorG = 1;
            }
        }

        if (uf === 'MA') {
            size = 9;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '2', '1', '', '', '', '', ''];
        }

        if (uf === 'MT') {
            if (documentIE.length === 9) {
                documentIE = padLeft(documentIE, 11, '0');
            }

            size = 11;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '', '', '',];
        }

        if (uf === 'MS') {
            size = 9;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '8', '2', '', '', '', '', ''];
        }

        if (uf === 'MG') {
            size = 13;
            xROT = 'AE';
            xMD = 10;
            xTP = 10;
            yROT = 'E';
            yMD = 11;
            yTP = 11;
            vDigits = ['DVY', 'DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '',];
        }

        if (uf === 'PA') {
            size = 9;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '5', '1,7', '', '', '', '', ''];
        }

        if (uf === 'PB') {
            size = 9;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '6', '1', '', '', '', '', ''];
        }

        if (uf === 'PR') {
            size = 10;
            xTP = 9;
            yROT = 'E';
            yMD = 11;
            yTP = 8;
            vDigits = ['DVY', 'DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '', '', '', '',];
        }

        if (uf === 'PE') {
            if (documentIE.length === 14) {
                // Antigo CACEPE
                size = 14;
                xTP = 7;
                factorF = 1;
                vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '1-9', '8', '1',];
            } else {
                size = 9;
                xTP = 14;
                yROT = 'E';
                yMD = 11;
                yTP = 7;
                vDigits = ['DVY', 'DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '', '', '', '', '',];
            }
        }

        if (uf === 'PI') {
            size = 9;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '9', '1', '', '', '', '', ''];
        }

        if (uf === 'RJ') {
            size = 8;
            xTP = 8;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '1,7,8,9', '', '', '', '', '', '',];
        }

        if (uf === 'RN') {
            if (documentIE.length === 9) {
                size = 9;
                xROT = 'BD';
                vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '0', '2', '', '', '', '', ''];
            } else {
                size = 10;
                xROT = 'BD';
                xTP = 11;
                vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '0', '2', '', '', '', '',];
            }
        }

        if (uf === 'RS') {
            size = 10;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '', '', '', ''];
        }

        if (uf === 'RO') {
            factorF = 1;

            if (documentIE.length === 9) {
                size = 9;
                xTP = 4;
                vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '1-9', '', '', '', '', '',];
            } else {
                size = 14;
                vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9,];
            }
        }

        if (uf === 'RR') {
            size = 9;
            xROT = 'D';
            xMD = 9;
            xTP = 5;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '4', '2', '', '', '', '', ''];
        }

        if (uf === 'SC' || uf === 'SE') {
            size = 9;
            vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '', '', '', '', ''];
        }

        if (uf === 'SP') {
            xROT = 'D';
            xTP = 12;

            // Delphi é 1-based, JS é 0-based
            if (documentIE[0] === 'P') {
                size = 13;
                vDigits = [C0_9, C0_9, C0_9, 'DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, 'P', '',];
            } else {
                size = 12;
                yROT = 'D';
                yMD = 11;
                yTP = 13;
                vDigits = ['DVY', C0_9, C0_9, 'DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '', '',];
            }
        }

        if (uf === 'TO') {
            if (documentIE.length === 11) {
                size = 11;
                xTP = 6;
                vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '1,2,3,9', '0,9', '9', '2', '', '', '',];
            } else {
                // size = 10;
                // vDigits = ['DVX',C0_9,C0_9,C0_9,C0_9,C0_9,C0_9,C0_9,C0_9,'0-4','','','',''];
                size = 9;
                vDigits = ['DVX', C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, C0_9, '', '', '', '', '',];
            }
        }

        // Verificando se o size Total está correto
        if (options && options.adjustSize) {
            documentIE = padLeft(documentIE, size, '0');
        }

        valid = size > 0 && documentIE.length === size;
        if (!valid) {
            errorMessage = `Tamanho inválido: Esperado ${size} caracteres, mas foram digitados ${size > documentIE.length ? 'somente ' : ''} ${documentIE.length} caracteres.`;
        }

        // Verificando os digitos nas posições são permitidos
        documentIE = padLeft(documentIE, 14);
        dvX = 0;
        dvY = 0;
        index = 13;

        while (valid && index >= 0) {
            // Ver se esse 14 está correto aqui pois o Delphi é 1-based, JS é 0-based
            digit = documentIE[13 - index];

            if (vDigits[index] === '') {
                valid = digit === ' ';
            } else if (
                vDigits[index] === 'DVX' ||
                vDigits[index] === 'DVY' ||
                vDigits[index] === C0_9
            ) {
                valid = charIsNum(digit);

                if (vDigits[index] === 'DVX') {
                    dvX = strToIntDef(digit, 0);
                } else {
                    if (vDigits[index] === 'DVY') {
                        dvY = strToIntDef(digit, 0);
                    }
                }
            } else if (vDigits[index].includes(',')) {
                // Ex: '2,5,7,8' Apenas os da lista
                valid = vDigits[index].split(',').includes(digit);
            } else if (vDigits[index].includes('-')) {
                const [start, end] = vDigits[index].split('-');
                valid = digit >= start && digit <= end;
            } else {
                valid = digit === vDigits[index];
            }

            if (!valid) {
                errorMessage = `Dígito ${13 - index - (13 - size)} deveria ser ${vDigits[index]}`;
            }

            index = index - 1;
        }

        step = 'X';
        while (valid && xTP > 0) {
            sum = 0;
            sumQ = 0;
            index = 14;

            while (valid && index > 0) {
                digit = documentIE[14 - index];

                if (charIsNum(digit)) {
                    nD = strToIntDef(digit, 0);
                    weight = nD * positionWeights[xTP - 1][index - 1];
                    sum = sum + weight;

                    if (xROT.includes('A')) {
                        sumQ = sumQ + Math.trunc(weight / 10);
                    }
                }

                index = index - 1;
            }

            if (xROT.includes('A')) {
                sum = sum + sumQ;
            } else if (xROT.includes('B')) {
                sum = sum * 10;
            } else if (xROT.includes('C')) {
                sum = sum + (5 + 4 * factorF);
            }

            // Calculando digito verificador
            checkDigit = Math.trunc(sum % xMD);
            if (xROT.includes('E')) {
                checkDigit = Math.trunc(xMD - checkDigit);
            }

            if (checkDigit === 10) {
                checkDigit = factorG; // Apenas GO modifica o FactorG para diferente de 0
            } else if (checkDigit === 11) {
                checkDigit = factorF;
            }

            if (step === 'X') {
                valid = dvX === checkDigit;
            } else {
                valid = dvY === checkDigit;
            }

            calculatedDigit = checkDigit.toString();
            if (!valid) {
                errorMessage = 'Dígito verificador inválido.';

                if (options && options.displayCorrectDigit) {
                    errorMessage = errorMessage + '.. Calculado: ' + calculatedDigit;
                }
            }

            if (step === 'X') {
                step = 'Y';
                xROT = yROT;
                xMD = yMD;
                xTP = yTP;
            } else {
                break;
            }
        }

        documentIE = documentIE.trim();
        if (errorMessage !== '') {
            errorMessage = `Inscrição estadual inválida para ${this._stateName(uf, true)}. Motivo: ${errorMessage}`;
        }

        return result(valid, errorMessage);
    }

    /**
 * Formata a inscrição estadual (IE) conforme o estado (UF) informado.
 *
 * Aplica a máscara correta de acordo com o estado e o número da inscrição estadual fornecido,
 * removendo quaisquer caracteres não numéricos e inserindo os separadores (como ponto, hífen, etc.)
 * conforme o padrão oficial da IE para aquele estado.
 *
 * Se o valor da IE for "ISENTO" (ignorando espaços e case), ele será retornado como está.
 *
 * @param uf - A sigla do estado (UF) para o qual a IE deve ser formatada (ex: 'SP', 'MG').
 * @param stateRegistration - O valor da inscrição estadual (IE), podendo conter ou não máscara.
 * @returns A inscrição estadual formatada com a máscara apropriada ou o valor original em caso de isenção ou erro.
 * @example
 * DocumentUtil.formatIE('SP', '123456789012'); // Retorna '123.456.789.012'
 * DocumentUtil.formatIE('MG', '1234567890123'); // Retorna '123.456.789/0123'
 */
    public static formatIE(uf: StateAcronym, stateRegistration: string): string {
        if (StringUtil.isEmpty(uf) || StringUtil.isEmpty(stateRegistration)) {
            return stateRegistration;
        }

        const trimmed = stateRegistration.trim().toUpperCase();
        if (trimmed === 'ISENTO') {
            return trimmed;
        }

        const digits = stateRegistration.replace(/[^a-zA-Z0-9]/g, '');
        const mask = this._getIeMask(uf, digits);
        const maskedLength = mask.length;
        const digitsLength = digits.length;

        let result = '';
        let j = maskedLength;

        for (let i = maskedLength - 1; i >= 0; i--) {
            const maskChar = mask[i];
            if (maskChar === '*') {
                if (j <= maskedLength - digitsLength) {
                    result = '0' + result;
                } else {
                    result = digits[j - (maskedLength - digitsLength) - 1] + result;
                }
                j--;
            } else {
                result = maskChar + result;
            }
        }

        return result;
    }

    /**
     * Retorna a máscara apropriada para a inscrição estadual (IE) com base na sigla do estado (UF)
     * e no número de dígitos da inscrição (útil para estados com variação de formato).
     *
     * A máscara é composta por asteriscos (*) e separadores (ponto, hífen, barra), que definem a posição
     * de cada caractere numérico na formatação final.
     *
     * @param uf - A sigla do estado (UF) para o qual a máscara da IE deve ser retornada.
     * @param digits - A string contendo apenas os dígitos da inscrição estadual, sem formatação.
     * @returns A máscara de formatação adequada para o estado informado.
     *
     * @example
     * DocumentUtil._getIeMask('SP', '123456789012'); // Retorna '***.***.***.***'
     */
    private static _getIeMask(uf: StateAcronym, digits: string): string {
        const len = digits.length;

        switch (uf) {
            case 'AC':
                return '**.***.***/***-**';
            case 'AL':
                return '*********';
            case 'AP':
                return '*********';
            case 'AM':
                return '**.***.***-*';
            case 'BA':
                return '*******-**';
            case 'CE':
                return '********-*';
            case 'DF':
                return '***********-**';
            case 'ES':
                return '********-*';
            case 'GO':
                return '**.***.***-*';
            case 'MA':
                return '*********';
            case 'MT':
                return '**********-*';
            case 'MS':
                return '**.***.***-*';
            case 'MG':
                return '***.***.***/****';
            case 'PA':
                return '**-******-*';
            case 'PB':
                return '********-*';
            case 'PI':
                return '********-*';
            case 'PR':
                return '***.*****-**';
            case 'RJ':
                return '**.***.**-*';
            case 'RS':
                return '***/*******';
            case 'RR':
                return '********-*';
            case 'SC':
                return '***.***.***';
            case 'SE':
                return '**.***.***-*';
            case 'PE':
                return len > 9 ? '**.*.***.*******-*' : '*******-**';
            case 'RN':
                return len > 9 ? '**.*.***.***-*' : '**.***.***-*';
            case 'RO':
                return len > 13 ? '*************-*' : '***.*****-*';
            case 'SP':
                return len > 1 && digits[0].toUpperCase() === 'P'
                    ? '*-********.*/***'
                    : '***.***.***.***';
            case 'TO':
                return len === 11 ? '**.**.******-*' : '**.***.***-*';
            default:
                return '*'.repeat(len);
        }
    }
}
